// Copyright (c) Ping. All rights reserved.

import type React from 'react';
import { useCallback, useState } from 'react';
import {
  Pressable,
  Modal as RNModal,
  ScrollView,
  StyleSheet,
  Text,
  type TextStyle,
  View,
  type ViewStyle,
} from 'react-native';
import { Avatar } from '../../components/Avatar';
import { createTheme } from '../../design-system/theme';
import { useColorScheme } from '../../foundations/colorScheme';
import { callHistoryStrings } from '../strings';
import type { UserId } from '../../util/ids';

export interface AddToFavoritesCandidate {
  readonly id: UserId;
  readonly displayName: string;
  readonly handle: string | null;
  readonly avatarUri: string | null;
  readonly isAlreadyFavorite: boolean;
}

export interface AddToFavoritesModalProps {
  readonly visible: boolean;
  readonly candidates: readonly AddToFavoritesCandidate[];
  readonly onConfirm: (selected: readonly UserId[]) => void;
  readonly onClose: () => void;
}

/**
 * Modal flow that lets the user pick which participants from a given
 * call to favorite. Selection state lives locally — host receives the
 * final list via `onConfirm`.
 */
export const AddToFavoritesModal: React.FC<AddToFavoritesModalProps> = ({
  visible,
  candidates,
  onConfirm,
  onClose,
}) => {
  const scheme = useColorScheme();
  const theme = createTheme(scheme);
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<UserId>>(() => new Set());

  const toggle = useCallback((id: UserId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleConfirm = useCallback(() => {
    onConfirm([...selectedIds]);
    setSelectedIds(new Set());
  }, [onConfirm, selectedIds]);

  const handleClose = useCallback(() => {
    setSelectedIds(new Set());
    onClose();
  }, [onClose]);

  return (
    <RNModal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={handleClose}
      accessibilityViewIsModal
      accessibilityLabel={callHistoryStrings.addFavoritesTitle}
    >
      <Pressable
        style={[styles.overlay, { backgroundColor: theme.colors.overlay.scrim }]}
        onPress={handleClose}
      >
        <Pressable
          onPress={(event) => event.stopPropagation()}
          style={[
            styles.shell,
            {
              backgroundColor: theme.colors.surface.primary,
              borderColor: theme.colors.border.secondary,
            },
          ]}
        >
          <View style={[styles.header, { borderBottomColor: theme.colors.border.secondary }]}>
            <Text style={[styles.title, { color: theme.colors.text.primary }]}>
              {callHistoryStrings.addFavoritesTitle}
            </Text>
            <Pressable
              onPress={handleClose}
              accessibilityRole="button"
              accessibilityLabel={callHistoryStrings.close}
              hitSlop={8}
            >
              <Text style={[styles.close, { color: theme.colors.text.secondary }]}>✕</Text>
            </Pressable>
          </View>
          <Text style={[styles.body, { color: theme.colors.text.secondary }]}>
            {candidates.length === 0
              ? callHistoryStrings.addFavoritesEmpty
              : callHistoryStrings.addFavoritesBody}
          </Text>
          <ScrollView style={styles.list} contentContainerStyle={styles.listContent}>
            {candidates.map((candidate) => {
              const checked = selectedIds.has(candidate.id);
              return (
                <Pressable
                  key={candidate.id}
                  onPress={() => toggle(candidate.id)}
                  accessibilityRole="checkbox"
                  accessibilityState={{ checked, disabled: candidate.isAlreadyFavorite }}
                  accessibilityLabel={candidate.displayName}
                  disabled={candidate.isAlreadyFavorite}
                  style={({ pressed }) => [
                    styles.candidateRow,
                    {
                      backgroundColor: pressed ? theme.colors.surface.secondary : 'transparent',
                      opacity: candidate.isAlreadyFavorite ? 0.55 : 1,
                    },
                  ]}
                >
                  <Avatar
                    size="small"
                    {...(candidate.avatarUri !== null ? { imageUri: candidate.avatarUri } : {})}
                    initials={candidate.displayName}
                  />
                  <View style={styles.candidateBody}>
                    <Text
                      style={[styles.candidateName, { color: theme.colors.text.primary }]}
                      numberOfLines={1}
                    >
                      {candidate.displayName}
                    </Text>
                    {candidate.handle ? (
                      <Text
                        style={[styles.candidateHandle, { color: theme.colors.text.secondary }]}
                        numberOfLines={1}
                      >
                        {candidate.handle}
                      </Text>
                    ) : null}
                  </View>
                  <View
                    style={[
                      styles.check,
                      {
                        borderColor: checked
                          ? theme.colors.accent.indigo
                          : theme.colors.border.primary,
                        backgroundColor: checked ? theme.colors.accent.indigo : 'transparent',
                      },
                    ]}
                  >
                    {checked ? (
                      <Text style={[styles.checkText, { color: theme.colors.text.onAccent }]}>
                        ✓
                      </Text>
                    ) : null}
                  </View>
                </Pressable>
              );
            })}
          </ScrollView>
          <View style={[styles.footer, { borderTopColor: theme.colors.border.secondary }]}>
            <Pressable
              onPress={handleClose}
              accessibilityRole="button"
              accessibilityLabel={callHistoryStrings.addFavoritesCancel}
              style={styles.footerButton}
            >
              <Text style={[styles.footerText, { color: theme.colors.text.secondary }]}>
                {callHistoryStrings.addFavoritesCancel}
              </Text>
            </Pressable>
            <Pressable
              onPress={handleConfirm}
              disabled={selectedIds.size === 0}
              accessibilityRole="button"
              accessibilityLabel={callHistoryStrings.addFavoritesConfirm}
              style={[
                styles.footerButton,
                styles.footerPrimary,
                {
                  backgroundColor:
                    selectedIds.size === 0
                      ? theme.colors.surface.tertiary
                      : theme.colors.accent.indigo,
                },
              ]}
            >
              <Text
                style={[
                  styles.footerText,
                  {
                    color:
                      selectedIds.size === 0
                        ? theme.colors.text.tertiary
                        : theme.colors.text.onAccent,
                    fontWeight: '600',
                  },
                ]}
              >
                {callHistoryStrings.addFavoritesConfirm}
              </Text>
            </Pressable>
          </View>
        </Pressable>
      </Pressable>
    </RNModal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  } satisfies ViewStyle,
  shell: {
    width: '100%',
    maxWidth: 460,
    maxHeight: '85%',
    borderRadius: 14,
    borderWidth: 1,
    overflow: 'hidden',
  } satisfies ViewStyle,
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderBottomWidth: 1,
  } satisfies ViewStyle,
  title: {
    fontSize: 16,
    fontWeight: '600',
  } satisfies TextStyle,
  close: {
    fontSize: 16,
    fontWeight: '600',
  } satisfies TextStyle,
  body: {
    fontSize: 13,
    paddingHorizontal: 16,
    paddingVertical: 12,
  } satisfies TextStyle,
  list: {
    maxHeight: 320,
  } satisfies ViewStyle,
  listContent: {
    paddingBottom: 8,
  } satisfies ViewStyle,
  candidateRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 16,
    paddingVertical: 10,
  } satisfies ViewStyle,
  candidateBody: {
    flex: 1,
  } satisfies ViewStyle,
  candidateName: {
    fontSize: 14,
    fontWeight: '600',
  } satisfies TextStyle,
  candidateHandle: {
    fontSize: 12,
  } satisfies TextStyle,
  check: {
    width: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 1.5,
    alignItems: 'center',
    justifyContent: 'center',
  } satisfies ViewStyle,
  checkText: {
    fontSize: 13,
    fontWeight: '700',
  } satisfies TextStyle,
  footer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderTopWidth: 1,
  } satisfies ViewStyle,
  footerButton: {
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 10,
  } satisfies ViewStyle,
  footerPrimary: {
    minWidth: 96,
    alignItems: 'center',
  } satisfies ViewStyle,
  footerText: {
    fontSize: 14,
  } satisfies TextStyle,
});
